import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getSocket } from '../utils/socket';
import { playJoinSuccess } from '../utils/sounds';

export default function PlayerHome() {
  const storedName = useMemo(() => localStorage.getItem('groupName'), []);
  // If there's a stored name, start in reconnecting mode (no flash of join form)
  const [showForm, setShowForm] = useState(!storedName);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { groupId, setGroupName } = useGame();
  const navigate = useNavigate();

  // Navigate when groupId is set — covers both reconnect and normal join
  useEffect(() => {
    if (groupId) navigate('/game');
  }, [groupId, navigate]);

  // Reconnect timeout: if groupId not set within 8s, fall back to join form
  useEffect(() => {
    if (!storedName || showForm) return;
    const t = setTimeout(() => setShowForm(true), 8000);
    return () => clearTimeout(t);
  }, [storedName, showForm]);

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Nom de groupe requis'); return; }
    setLoading(true);
    setError('');

    const socket = getSocket();

    const doJoin = () => {
      socket.emit('player:join', { name: trimmed }, () => {});
    };

    if (!socket.connected) {
      socket.once('connect', doJoin);
      socket.connect();
    } else {
      doJoin();
    }

    const handleJoinResult = (data: any) => {
      setLoading(false);
      if (data.success) {
        playJoinSuccess();
        setGroupName(trimmed);
        navigate('/game');
      } else {
        setError(data.error || 'Impossible de rejoindre');
      }
    };

    socket.once('player:join', handleJoinResult);

    const timeout = setTimeout(() => {
      socket.off('player:join', handleJoinResult);
      setLoading(false);
      setError('Délai dépassé — vérifiez votre connexion et réessayez.');
    }, 8000);

    socket.once('player:join', () => clearTimeout(timeout));
  };

  // Reconnecting screen (no flash of join form)
  if (!showForm) {
    return (
      <div className="player-home">
        <div className="join-card">
          <div className="join-icon">🎵</div>
          <h1>BlindTest</h1>
          <p style={{ marginBottom: '0.5rem' }}>Reconnexion en cours...</p>
          <p className="text-muted">
            Équipe : <strong style={{ color: 'var(--text)' }}>{storedName}</strong>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0' }}>
            <span className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
          <button
            className="btn btn-secondary btn-full"
            onClick={() => { localStorage.removeItem('groupName'); setShowForm(true); }}
          >
            Se connecter avec un autre nom
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="player-home">
      <div className="join-card">
        <div className="join-icon">🎵</div>
        <h1>BlindTest</h1>
        <p className="join-subtitle">Entrez le nom de votre équipe pour rejoindre</p>
        <form onSubmit={handleJoin} className="join-form">
          {error && <div className="alert alert-danger">{error}</div>}
          <input
            className="join-input"
            type="text"
            placeholder="Nom de l'équipe"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
            maxLength={50}
            disabled={loading}
            autoFocus
            autoComplete="off"
            autoCapitalize="words"
          />
          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !name.trim()}>
            {loading ? (
              <span className="btn-spinner">
                <span className="spinner" />
                Connexion...
              </span>
            ) : (
              'Rejoindre →'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
